/**
 * Wraps OpenPanel (`@openpanel/web`) behind a stable `analytics` API, gated on
 * `import.meta.env.PROD` (`env_check`) so nothing is actually sent outside production.
 *
 * `@openpanel/web` bundles rrweb (~40 KB br) — not needed before hydration, so the class and the
 * instance it constructs are lazy: `withOp` runs a callback against the instance once it exists
 * (immediately, if already loaded), otherwise queues the callback and kicks off the one-time
 * dynamic import. Properties that depend on the moment of the call (session_id, normalised props)
 * are computed at call time, before queueing — never at flush time, so a delayed flush can't pick
 * up a stale value.
 */
import type { OpenPanel } from "@openpanel/web";

let _op: OpenPanel | null = null;
let loadPromise: Promise<void> | null = null;

// ── Hold events until the signed-in user is known ────────────────────────────
// The SDK keeps its `profileId` in memory only, and `@openpanel/web` fires a `screen_view` at
// construction (`setTimeout(..., 0)`) — which routinely beats Firebase restoring the session after
// a reload. Without a hold, that first view (and anything else sent before the sign-in resolves)
// goes out anonymous and never joins the user's profile. With `waitForProfile` the SDK parks every
// event until `identify()` gives it a profileId (applied at flush time) or `ready()` releases it
// for a visitor who turns out to be signed out.
//
// Opt-in, per bundle: only `index.tsx` (the SPA, whose `App` owns the Firebase auth listener)
// calls `holdUntilAuthResolved()`. The marketing SSR shell and the B2B public-website bundle share
// this module but have no auth listener at all, so nothing there would ever release a hold —
// their events would be parked forever. Never make this the default.
let holdForAuth = false;
let holdReleased = false;
let holdFallbackTimer: ReturnType<typeof setTimeout> | undefined;

// An identify that arrived before the instance existed (and before any import was in flight).
// Applied by `loadOp` straight after construction — before the queued calls run and before the
// SDK's own first `screen_view` — so loading for a signed-in user stays on the idle schedule too.
let pendingIdentify: { profileId: string; properties?: Record<string, unknown> } | undefined;

// Upper bound on how long events wait for auth. If Firebase never resolves (blocked, offline,
// broken config), `ready()` is never called by App and analytics would stay silent for the whole
// visit; releasing anonymously after this is strictly better than losing the visit.
const AUTH_HOLD_FALLBACK_MS = 10_000;

function releaseHold(): void {
	holdReleased = true;
	if (holdFallbackTimer !== undefined) {
		clearTimeout(holdFallbackTimer);
		holdFallbackTimer = undefined;
	}
}

// Calls made before the instance exists queue here. Capped so a page that never becomes
// interactive (the import never resolves — offline, blocked, etc.) can't grow this unboundedly.
const MAX_QUEUE_LENGTH = 200;
let queue: Array<(op: OpenPanel) => void> = [];

function loadOp(): void {
	// Gated here too, not only in init(): constructing the SDK sends screen views on its own (`trackScreenViews`), so a
	// track() in a non-production build or on a preview host would otherwise still report pageviews.
	if (_op || loadPromise || !env_check) return;
	loadPromise = import("@openpanel/web")
		.then(({ OpenPanel }) => {
			const op = new OpenPanel({
				clientId: "57f3e741-5a2e-4c8c-b0eb-f8c4eefefac6",
				apiUrl: "https://test.stippl.io/api",
				trackScreenViews: true,
				trackOutgoingLinks: true,
				trackAttributes: true,
				// `holdReleased` already true: ready() ran before this instance existed (a signed-out
				// visitor, resolved ahead of the idle `init()`), so there is nothing to wait for.
				waitForProfile: holdForAuth && !holdReleased,
			});
			if (holdForAuth && !holdReleased) {
				holdFallbackTimer = setTimeout(() => {
					holdFallbackTimer = undefined;
					if (holdReleased) return;
					holdReleased = true;
					op.ready();
				}, AUTH_HOLD_FALLBACK_MS);
			}
			// Base props are set once, here, as part of creating the instance — not per call — so
			// every queued callback below runs against an instance that already carries them.
			ensureBaseProps(op);
			if (pendingIdentify) {
				op.identify(pendingIdentify);
				pendingIdentify = undefined;
			}
			_op = op;
			const pending = queue;
			queue = [];
			pending.forEach((fn) => fn(op));
		})
		.catch((e) => {
			// Allow the next call to retry the import; the queued events stay queued (capped).
			loadPromise = null;
			console.error("[Analytics] OpenPanel failed to load:", { error: e?.message || e });
		});
}

// Runs `fn` against the OpenPanel instance once it exists — immediately if it already does,
// otherwise queued until the lazy import above resolves. No-op outside the browser (SSR).
function withOp(fn: (op: OpenPanel) => void): void {
	if (typeof window === "undefined") return;
	if (_op) {
		fn(_op);
		return;
	}
	queue.push(fn);
	if (queue.length > MAX_QUEUE_LENGTH) queue.shift();
	loadOp();
}

// PR previews (stippl-pr-<n>.fly.dev) are production builds too, and sent 4.1% of all pageviews into the production
// project on 21-27 Oct 2026, most of them on the pages under test. Nothing is sent from a preview host.
let env_check = import.meta.env.PROD && !(typeof window !== "undefined" && window.location.hostname.endsWith(".fly.dev"));
let currentProfileId: string | undefined;

// Web build version — set as a Docker build arg in CI (VITE_SENTRY_RELEASE = git sha),
// falls back to "dev" locally. Injected on every event as app_version.
const APP_VERSION = (import.meta.env.VITE_SENTRY_RELEASE as string) || "dev";

// ── Session model (web) ──────────────────────────────────────────────────────
// A session_id that rotates after 30 min of inactivity, attached to every event so
// events within a visit can be joined (affiliate dwell, return-after-clickout, etc.).
// OpenPanel's built-in session metric covers WAU/duration; this id is for cross-event joins.
const SESSION_TTL_MS = 30 * 60 * 1000;
const SESSION_ID_KEY = "op_session_id";
const SESSION_LAST_KEY = "op_session_last";

function uuid(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

function getSessionId(): string | undefined {
	if (typeof window === "undefined") return undefined;
	try {
		const now = Date.now();
		const last = Number(window.sessionStorage.getItem(SESSION_LAST_KEY) || 0);
		let id = window.sessionStorage.getItem(SESSION_ID_KEY) || "";
		if (!id || !last || now - last > SESSION_TTL_MS) {
			id = uuid();
			window.sessionStorage.setItem(SESSION_ID_KEY, id);
		}
		window.sessionStorage.setItem(SESSION_LAST_KEY, String(now));
		return id;
	} catch {
		return undefined; // sessionStorage unavailable (private mode / blocked)
	}
}

// ── Campaign attribution (web) ───────────────────────────────────────────────
// The OpenPanel web SDK does NOT parse utm_* into properties. Verified 2026-09-09:
// across 334,773 screen_view events in 30 days, a breakdown on `properties.utm_source`
// returns a single unlabelled series — the property does not exist. Everything the
// Listmonk tagging put on those links was landing only inside the raw referrer string.
//
// Captured at module load rather than on the first event, because this is a SPA: the
// router replaces the URL, so by the time an event fires the params can already be gone.
// Held in sessionStorage so they survive client-side navigation and reloads within the
// visit, and so they behave as first-touch — a later untagged page view must not clear
// the campaign that started the session.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const UTM_STORAGE_KEY = "op_utm";

function captureUtmParams(): void {
	if (typeof window === "undefined") return;
	try {
		const params = new URLSearchParams(window.location.search);
		const found: Record<string, string> = {};
		for (const key of UTM_KEYS) {
			const value = params.get(key);
			if (value) found[key] = value;
		}
		// No params on this URL: keep whatever started the session.
		if (!Object.keys(found).length) return;
		window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
	} catch {
		// sessionStorage unavailable (private mode / blocked), or a malformed query string
	}
}

function getUtmProps(): Record<string, string> {
	if (typeof window === "undefined") return {};
	try {
		return JSON.parse(window.sessionStorage.getItem(UTM_STORAGE_KEY) || "{}");
	} catch {
		return {};
	}
}

captureUtmParams();

// ── Escape attribution (web) ─────────────────────────────────────────────────
// Someone who taps "Open in browser" on the sign-in notice inside Snapchat, Instagram, etc. lands
// in Safari or Chrome as a brand-new OpenPanel visitor, so their signup could not be tied back to
// the in-app view it came from. `utils/inAppBrowser.ts` (`withEscapeMarker`) tags the outgoing URL
// with `iab_from` (which app) and `iab_via` (which way out); this reads them the same way the UTM
// block above does: at module load, into sessionStorage, stamped on every event by
// `ensureBaseProps`. Those param names are the contract with that util; its test feeds its own
// output through here so the two cannot drift.
//
// Unlike utm_*, the params are then REMOVED from the address bar. A campaign tag on a shared link
// is fine, but this one marks "this visit came out of Snapchat" and must not ride along when the
// person copies the URL to a friend. It runs at module load, before OpenPanel has loaded, so the
// SDK has not yet patched `history.replaceState` (its patch fires a second screen_view) and the
// landing screen_view's `__path` is already the clean URL: the marker travels as a global
// property, not in the path.
const ESCAPE_STORAGE_KEY = "op_iab";
const ESCAPE_PARAM_KEYS = ["iab_from", "iab_via"] as const;
// The values are a short closed vocabulary (lowercase words and underscores). Anyone can type
// anything into a URL, so anything else is ignored rather than written into analytics.
const ESCAPE_VALUE = /^[a-z0-9_]{1,40}$/;

function captureEscapeParams(): void {
	if (typeof window === "undefined") return;
	try {
		const params = new URLSearchParams(window.location.search);
		// Nothing tagged on this URL: keep whatever started the session, and leave the URL alone.
		if (!ESCAPE_PARAM_KEYS.some((key) => params.has(key))) return;

		const from = params.get("iab_from") || "";
		const via = params.get("iab_via") || "";
		if (ESCAPE_VALUE.test(from)) {
			const found: Record<string, string> = { iab_from: from };
			if (ESCAPE_VALUE.test(via)) found.iab_via = via;
			try {
				window.sessionStorage.setItem(ESCAPE_STORAGE_KEY, JSON.stringify(found));
			} catch {
				// sessionStorage blocked: the stamp is lost, but the URL below is still cleaned.
			}
		}

		// Strip from the raw query string instead of re-serialising it through URLSearchParams, so
		// the rest of the query reaches the router exactly as it came. `window.history.state` is
		// passed back in because the router keeps its own location key in it.
		const kept = window.location.search
			.slice(1)
			.split("&")
			.filter((pair) => pair !== "" && !(ESCAPE_PARAM_KEYS as readonly string[]).includes(pair.split("=")[0]));
		window.history.replaceState(window.history.state, "", `${window.location.pathname}${kept.length ? `?${kept.join("&")}` : ""}${window.location.hash}`);
	} catch {
		// sessionStorage unavailable (private mode / blocked), or a malformed query string
	}
}

function getEscapeProps(): Record<string, string> {
	if (typeof window === "undefined") return {};
	try {
		return JSON.parse(window.sessionStorage.getItem(ESCAPE_STORAGE_KEY) || "{}");
	} catch {
		return {};
	}
}

captureEscapeParams();

// Inject wrapper-level base props once. product_line defaults to "b2c";
// useAnalyticsIdentify overrides it to "b2b" for org users.
let baseRegistered = false;
function ensureBaseProps(op: OpenPanel) {
	if (baseRegistered) return;
	baseRegistered = true;
	// Only present keys are spread, so an untagged visit does not stamp every event with
	// utm_source: undefined. Same for iab_from / iab_via, which ride on every event of an escaped
	// session (screen_view, signup_attempt, signup_error, user_signup, ...) with no per-event wiring.
	op.setGlobalProperties({ platform: "web", app_version: APP_VERSION, product_line: "b2c", ...getUtmProps(), ...getEscapeProps() });
}

// Currency codes must be uppercase ISO-4217 — lowercase caused $0 totals (commit 109df147e).
function normalizeProps(props: any) {
	if (props && typeof props === "object" && typeof props.currency === "string") {
		return { ...props, currency: props.currency.toUpperCase() };
	}
	return props;
}

let actions = {
	// SPA pages that never fire a `track`/`identify` call before this (route preview, public
	// profile, lists, auth pages) never triggered `loadOp()`, so `trackScreenViews` never started
	// and their signed-out visits were invisible in OpenPanel — e.g. `/routepreview/:tripId` sends
	// zero requests to OpenPanel on load or scroll, desktop or mobile, while a marketing page that
	// fires `landing_page_viewed` on mount does. App.tsx calls this once on mount so every SPA page
	// is covered regardless of whether a trackEvent ever fires. No-op outside the browser and in
	// non-prod builds, same gating every other action here already applies.
	init: () => {
		if (typeof window === "undefined") return;
		if (!env_check) return;
		loadOp();
	},
	// Opts this bundle in to holding events until auth resolves — see the block comment on
	// `holdForAuth` above. Must run synchronously at entry-module evaluation, i.e. before the
	// OpenPanel instance is constructed (the option is read once, at construction). Only the SPA
	// entry (`index.tsx`) calls it, because only that bundle resolves Firebase auth; any other
	// bundle calling it would park its events forever.
	holdUntilAuthResolved: () => {
		holdForAuth = true;
	},
	identify: (id, properties?) => {
		// Same reasoning as `ready()` below: with no instance and no import in flight, nothing is
		// parked, and going through `withOp` would pull the SDK onto the critical path of every
		// signed-in page load (App identifies in the same commit that first renders the page). Record
		// it instead; `loadOp` builds the instance unheld and identifies it before anything is sent.
		// Properties merge across calls for the same profile (App's bare identify and the B2B hook's
		// `{ email }` can arrive in either order).
		if (typeof window !== "undefined" && env_check && !_op && !loadPromise) {
			currentProfileId = id;
			const mergedProperties = {
				...(pendingIdentify?.profileId === id ? pendingIdentify.properties : undefined),
				...properties,
			};
			pendingIdentify = { profileId: id, ...(Object.keys(mergedProperties).length ? { properties: mergedProperties } : {}) };
			releaseHold();
			return;
		}
		withOp((op) => {
			if (!env_check) return;
			currentProfileId = id;
			// Order matters: identify first, so the held queue flushes WITH this profileId; ready()
			// after, so `waitForProfile` is cleared and a later `clear()` (sign-out) cannot park
			// the queue again. Both are no-ops for a bundle that never held anything.
			op.identify({ profileId: id, ...(properties ? { properties } : {}) });
			releaseHold();
			op.ready();
		});
	},
	// Releases held events anonymously: the auth check came back with nobody signed in.
	ready: () => {
		if (typeof window === "undefined" || !env_check) return;
		// No instance and no import in flight: nothing is parked yet, and going through `withOp`
		// would start the lazy import right now — putting OpenPanel's rrweb chunk on the critical
		// path of every signed-out visit, which App's idle-scheduled `init()` exists to avoid. Record
		// the verdict instead; `loadOp` reads it when the instance is eventually built and constructs
		// it unheld.
		if (!_op && !loadPromise) {
			releaseHold();
			return;
		}
		withOp((op) => {
			releaseHold();
			op.ready();
		});
	},
	// Sign-out without a page reload: forget the profile (and the SDK's device/session ids) so the
	// next user does not inherit it. `ready()` straight after is required — `clear()` leaves
	// `waitForProfile` as it was, and with it still set and no profileId the queue would hold
	// every later event forever.
	reset: () => {
		// Nothing loaded yet: drop the recorded identify, so the instance is never built with it.
		if (typeof window !== "undefined" && env_check && !_op && !loadPromise) {
			currentProfileId = undefined;
			pendingIdentify = undefined;
			releaseHold();
			return;
		}
		withOp((op) => {
			if (!env_check) return;
			currentProfileId = undefined;
			releaseHold();
			op.clear();
			op.ready();
		});
	},
	alias: (_id) => {
		// OpenPanel deprecated alias — no callers in codebase
	},
	track: (name, props) => {
		// Computed now, at call time — not inside the withOp callback, which may run later.
		const sessionId = getSessionId();
		// OpenPanel drops the page path on many custom events since 2026-10-08; this property is what per-page conversion reads use. Pathname only: the query and hash can carry utm/PII.
		const pagePath = typeof window !== "undefined" ? window.location.pathname : undefined;
		const normalisedProps = normalizeProps(props);
		withOp((op) => {
			if (!env_check) return;
			op.track(name, { session_id: sessionId, ...(pagePath ? { page_path: pagePath } : {}), ...normalisedProps });
		});
	},
	register: (props) => {
		withOp((op) => {
			if (!env_check) return;
			op.setGlobalProperties(props);
		});
	},
	people: {
		set: (props) => {
			withOp((op) => {
				if (!env_check || !currentProfileId) return;
				op.identify({ profileId: currentProfileId, properties: props });
			});
		},
	},
};

export let analytics = actions;
